/**
 * What APIM answers when authz refuses a GAP call: authz's status (403) and, as text/plain, its body after this prefix
 * (`{ "message": "User x has no access to application gap" }` or `... "Insufficient access level: requires write"`).
 */
import { HttpErrorResponse } from '@angular/common/http';

const AUTHZ_PREFIX = 'Authz service application response:';

export interface AuthzRefusal {
  /** The user has no access to GAP at all, not only too low a level for this call. */
  noAccess: boolean;
  /** What to tell the user. */
  message: string;
}

/** The refusal in an HTTP error, when it is APIM's authz check that turned the call down; null otherwise. */
export function getAuthzRefusal(error: any): AuthzRefusal | null {
  const body = error?.error;
  if (error?.status !== 403 || typeof body !== 'string' || !body.startsWith(AUTHZ_PREFIX)) {
    return null;
  }
  let reason = '';
  try {
    reason = JSON.parse(body.slice(AUTHZ_PREFIX.length))?.message ?? '';
  } catch {
    // authz's body was no JSON: the refusal still stands
  }
  const level = /requires (\w+)/i.exec(reason)?.[1];
  if (level) {
    return { noAccess: false, message: `You need ${level.toLowerCase()} access to GAP to do this. Ask a GAP administrator to grant it.` };
  }
  if (/missing permission/i.test(reason)) {
    return { noAccess: false, message: 'You do not have the GAP permission this needs. Ask a GAP administrator to grant it.' };
  }
  return { noAccess: true, message: 'You do not have access to GAP. Ask a GAP administrator to grant you access to the GAP application.' };
}

/**
 * APIM's refusal as the GAP services word an error, `{ Description }`: the pages show it as they show any other, with
 * no change to them.
 */
export function asGapError(error: HttpErrorResponse, refusal: AuthzRefusal): HttpErrorResponse {
  return new HttpErrorResponse({
    error: { Description: refusal.message },
    headers: error.headers,
    status: error.status,
    statusText: error.statusText,
    url: error.url ?? undefined
  });
}
